import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

export default function ContactPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    company: '',
    requested_service: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const services = [
    { value: 'booking', label: 'Booking Artístico' },
    { value: 'management', label: 'Management Artístico' },
    { value: 'events', label: 'Producción de Eventos' },
    { value: 'audiovisual', label: 'Producción Audiovisual' },
    { value: 'branding', label: 'Branding & Marketing' },
    { value: 'experiences', label: 'Experiencias Exclusivas' },
    { value: 'other', label: 'Otro' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, requested_service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        form_type: 'contact'
      });
      setSuccess(true);
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        company: '',
        requested_service: '',
        message: ''
      });
    } catch (err) {
      setError(t('contact.form.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16" data-testid="contact-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] to-[#050505]" />
        <div className="container-premium relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <img 
                src="/gs-agency-logo-gold.png" 
                alt="GS Agency" 
                className="h-48 md:h-64 w-auto object-contain"
              />
            </motion.div>
            <motion.p variants={fadeInUp} className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">
              {t('contact.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-display text-5xl md:text-7xl text-white mb-8">
              {t('contact.title')}
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-dark py-16" data-testid="contact-form-section">
        <div className="container-premium">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="font-display text-3xl text-white mb-6">
                  Hablemos de tu próximo proyecto
                </h2>
                <p className="text-white/60 leading-relaxed">
                  Ya sea que busques booking artístico, producción de eventos, o una estrategia completa de entretenimiento, estamos aquí para ayudarte a crear experiencias inolvidables.
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="space-y-6">
                <a 
                  href="mailto:info@gsagency.com" 
                  className="flex items-start gap-4 group"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm mb-1">Email</p>
                    <p className="text-white group-hover:text-[#D4AF37] transition-colors">
                      {t('contact.info.email')}
                    </p>
                  </div>
                </a>

                <a 
                  href="tel:+5255187377664" 
                  className="flex items-start gap-4 group"
                  data-testid="contact-phone"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <Phone size={20} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm mb-1">Teléfono</p>
                    <p className="text-white group-hover:text-[#D4AF37] transition-colors">
                      {t('contact.info.phone')}
                    </p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm mb-1">Ubicación</p>
                    <p className="text-white">
                      {t('contact.info.location')}
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              {success ? (
                <div className="premium-card p-10 text-center">
                  <CheckCircle size={64} className="mx-auto text-green-500 mb-6" />
                  <h3 className="font-display text-2xl text-white mb-4">
                    {t('contact.form.success')}
                  </h3>
                  <p className="text-white/60">
                    Te contactaremos pronto para discutir tu proyecto.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="premium-card p-8 space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white/60 text-sm mb-2">
                        {t('contact.form.name')} *
                      </label>
                      <Input
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        required
                        className="input-premium w-full"
                        placeholder="Tu nombre"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">
                        {t('contact.form.email')} *
                      </label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="input-premium w-full"
                        placeholder="tu@email.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white/60 text-sm mb-2">
                        {t('contact.form.phone')}
                      </label>
                      <Input
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="input-premium w-full"
                        placeholder="+52 55 1234 5678"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">
                        {t('contact.form.company')}
                      </label>
                      <Input
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="input-premium w-full"
                        placeholder="Tu empresa o marca"
                        data-testid="input-company"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white/60 text-sm mb-2">
                      {t('contact.form.service')}
                    </label>
                    <Select onValueChange={handleServiceChange} value={formData.requested_service}>
                      <SelectTrigger className="input-premium w-full" data-testid="select-service">
                        <SelectValue placeholder="Selecciona un servicio" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.value} value={service.value}>
                            {service.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-white/60 text-sm mb-2">
                      {t('contact.form.message')} *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="input-premium w-full resize-none"
                      placeholder="Cuéntanos sobre tu proyecto..."
                      data-testid="input-message"
                    />
                  </div>

                  {error && (
                    <p className="text-red-500 text-sm">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-gold w-full flex items-center justify-center gap-2"
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      'Enviando...'
                    ) : (
                      <>
                        {t('contact.form.submit')}
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
